import { useState, useEffect } from 'react';

import "react-datetime/css/react-datetime.css";
import './styles.css';

import InstructorLevelItemsModal from './levelItemsModal';
import BalancedEquationLevelModal from './balancedEquationLevelModal';
import PrinciplesLevelModal from './principlesLevelModal';
import ClassificationLevelModal from './classificationLevelModal';
import NormalBalanceLevelModal from './normalBalanceLevelModal';
import Alert from '../../../alert';

import IconPlus from './images/icon-plus.svg';
import InstructorAssignmentLevelSavedAccounts from './levelSavedAccounts';

export default function InstructorAssignmentSetLevels({
  gameType,
  scenarioLevels,
  onScenarioLevelAccountSelectSave,
  isButtonLoading,
  onShowLevelAccounts,
  filterItems,
  filterItemsDesign,
  onLevelDeletionConfirm,
  editingScenarioLevelIndex,
  editingScenarioLevelPreCheckedItems,
  isGameStarted,
  businessType
}) {
  const [activeTab, setActiveTab] = useState(0);
  
  const [isModalShown, setIsModalShown] = useState(false);
  const [isAlertVisible, setIsAlertVisible] = useState(false);

  const [alertType, setAlertType] = useState('warning');
  const [alertTitle, setAlertTitle] = useState('');
  const [alertText, setAlertText] = useState('');
  const [alertCancelButtonText, setAlertCancelButtonText] = useState('No');
  const [alertConfirmButtonText, setAlertConfirmButtonText] = useState('Yes');

  useEffect(() => {
    setActiveTab(0);
  }, [gameType]);

  useEffect(() => {
    if (!scenarioLevels?.length) return;

    setActiveTab(prev =>
      prev >= scenarioLevels.length ? 0 : prev
    );
  }, [scenarioLevels]);

  const handleTabClick = (tabIndex) => {
    setActiveTab(tabIndex);
  }

  const showLevelAccounts = (scenarioLevelIndex) => {
    onShowLevelAccounts(scenarioLevelIndex);
    setIsModalShown(true);
  }

  const handleAddLevelButtonClick = () => {
    if(isGameStarted) {
      setAlertType('error');
      setAlertTitle('Unsuccessful');
      setAlertText('This assignment is locked for editing as students have already started working on it.');
      setAlertConfirmButtonText('Ok');
      setAlertCancelButtonText('');
      
      setIsAlertVisible(true);
    } else {
      showLevelAccounts();
    }
  }

  const handleEditLevelButtonClick = (scenarioLevelIndex) => {
    if(isGameStarted) {
      setAlertType('error');
      setAlertTitle('Unsuccessful');
      setAlertText('This assignment is locked for editing as students have already started working on it.');
      setAlertConfirmButtonText('Ok');
      setAlertCancelButtonText('');
      setIsAlertVisible(true);
    } else {
      showLevelAccounts(scenarioLevelIndex);
    }
  }

  const handleModalCloseButtonClick = () => {
    setIsModalShown(false);
  }

  const handleModalButtonSaveClick = () => {
    setIsModalShown(false);
  }

  const handleDeleteButtonClick = () => {
    if(isGameStarted) {
      setAlertType('error');
      setAlertTitle('Unsuccessful');
      setAlertText('This assignment is locked for editing as students have already started working on it.');
      setAlertConfirmButtonText('Ok');
      setAlertCancelButtonText('');
    } else {
      setAlertType('warning');
      setAlertTitle('Are you sure you want to delete this level?');
      setAlertText('');
      setAlertConfirmButtonText('Yes');
      setAlertCancelButtonText('No');
    }

    setIsAlertVisible(true);
  }

  const handleCancelButtonClick = () => {
    setIsAlertVisible(false);
  }

  const handleDeletionCompleted = () => {
    setActiveTab(0);
    setIsAlertVisible(false);
  }

  const handleConfirmAlertButtonClick = () => {
    if(isGameStarted) {
      setIsAlertVisible(false);
    } else {
      onLevelDeletionConfirm(scenarioLevels[activeTab]?.id, handleDeletionCompleted);
    }
  }

  return (
    <div className="instructor-set-levels-container">
      <h3>Set Levels</h3>
      <div className="instructor-assignment-levels">
        <div className="instructor-assignment-level-tabs">
          {
            scenarioLevels?.map((scenarioLevel, index) => {
              const isActive = index === activeTab;
              return(
                <div
                  key={index}
                  className={`assignment-tab-header ${isActive ? 'active' : ''}`} 
                  onClick={() => handleTabClick(index)}
                >
                  {
                    (isActive
                      && (!gameType?.includes('Classification') || (gameType?.includes('Classification') && index !== 0))
                      && (!gameType?.includes('Normal Balance') || (gameType?.includes('Normal Balance') && index !== 0))
                    ) &&
                      <button className="assignment-edit-scenario-level" onClick={() => handleEditLevelButtonClick(index)}></button>
                  }
                  <span>Level {scenarioLevel.level}</span>
                  {
                    (isActive 
                      && (index === scenarioLevels.length - 1) 
                      && (!gameType?.includes('Classification') || (gameType?.includes('Classification') && index !== 0))
                      && (!gameType?.includes('Normal Balance') || (gameType?.includes('Normal Balance') && index !== 0))
                    ) &&
                      <button className="assignment-delete-scenario-level" onClick={handleDeleteButtonClick}></button>
                  }
                </div>
              )
            })
          }
          {
            scenarioLevels?.length < (gameType === 'The Principles' ? 1 : 3) &&
              <button
                className="assignment-tab-add-level"
                data-tour-id="add-level-button"
                onClick={() => handleAddLevelButtonClick(null)}
              >
                Add Level <img src={IconPlus} alt="Add"/>
              </button>
          }
        </div>
        <div className="instructor-assignment-tab-content">
          {
            gameType && scenarioLevels && scenarioLevels.length > 0 &&
              <InstructorAssignmentLevelSavedAccounts
                gameType={gameType}
                scenarioLevels={scenarioLevels}
                activeScenarioLevel={activeTab}
              />
          }
        </div>
      </div>

      {isModalShown && (() => {
        const computedScenarioLevelNumber =
          typeof editingScenarioLevelIndex === 'number'
            ? (editingScenarioLevelIndex + 1)
            : (scenarioLevels?.length + 1) || 0;
        const editingDecisionType = scenarioLevels?.[editingScenarioLevelIndex]?.name;
        // The new game-specific modals expect a stripped-down prop set.
        // Only games whose flow has been migrated route here; others
        // continue to use the existing InstructorLevelItemsModal.
        const sharedModalProps = {
          scenarioLevelNumber: computedScenarioLevelNumber,
          onCloseButtonClick: handleModalCloseButtonClick,
          onSaveButtonClick: onScenarioLevelAccountSelectSave,
          isButtonLoading,
          filterItems,
          decisionType: editingDecisionType,
          editingScenarioLevelPreCheckedItems,
        };

        if (gameType === 'Balanced Equation') {
          return <BalancedEquationLevelModal {...sharedModalProps} />;
        }
        if (gameType === 'The Principles') {
          return <PrinciplesLevelModal {...sharedModalProps} />;
        }
        if (gameType === 'Account Classification') {
          return <ClassificationLevelModal {...sharedModalProps} businessType={businessType} />;
        }
        if (gameType === 'Normal Balance') {
          return <NormalBalanceLevelModal {...sharedModalProps} />;
        }

        return (
          <InstructorLevelItemsModal
            scenarioLevelNumber={computedScenarioLevelNumber}
            onCloseButtonClick={handleModalCloseButtonClick}
            onSaveButtonClick={onScenarioLevelAccountSelectSave}
            onSave={handleModalButtonSaveClick}
            isButtonLoading={isButtonLoading}
            gameType={gameType}
            filterItems={filterItems}
            filterItemsDesign={filterItemsDesign}
            editingScenarioLevelPreCheckedItems={editingScenarioLevelPreCheckedItems}
            decisionType={editingDecisionType}
            businessType={businessType}
          />
        );
      })()}

      {
        isAlertVisible &&
          <Alert 
            type={alertType}
            title={alertTitle}
            text={alertText}
            confirmButtonText={alertConfirmButtonText}
            cancelButtonText={alertCancelButtonText}
            onCancelButtonClick={handleCancelButtonClick}
            onConfirmButtonClick={handleConfirmAlertButtonClick}
          />
      }
    </div>
  );
}
